import React from 'react';
import { GcalContext, GcalStatus } from '@geerd-nx/gcal';
import type { IGcalContext, GcalSession, GcalUser } from '@geerd-nx/gcal';
import {
  authClient,
  getBetterAuthJwt,
  clearBetterAuthJwt,
} from './betterAuthClient';

/**
 * Better Auth-backed implementation of the gcal auth context.
 *
 * It provides the SAME IGcalContext shape the app already consumes
 * (user / groups / status / getSession / signOut), so Apollo and every
 * useGcal() consumer keep working with no changes. Selected vs the Cognito
 * GcalProvider by the NX_USE_BETTER_AUTH flag in main.tsx.
 */
export function BetterAuthGcalProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { data, isPending } = authClient.useSession();
  const user = data?.user ?? null;
  const role = (user as { role?: string } | null)?.role;

  // Returns a CognitoUserSession-shaped shim whose token methods yield the Better
  // Auth JWT — so apollo/index.tsx (getAccessToken().getJwtToken()) is untouched.
  const getSession = React.useCallback(async (): Promise<GcalSession> => {
    const jwt = await getBetterAuthJwt();
    const tokenLike = {
      getJwtToken: () => jwt,
      decodePayload: () => {
        try {
          return JSON.parse(atob(jwt.split('.')[1]));
        } catch {
          return {};
        }
      },
    };
    return {
      getAccessToken: () => tokenLike,
      getIdToken: () => tokenLike,
    } as unknown as GcalSession;
  }, []);

  const signOut = React.useCallback((redirect?: string) => {
    authClient.signOut().finally(() => {
      clearBetterAuthJwt();
      localStorage.clear();
      window.location.pathname = redirect ?? '/auth/login';
    });
  }, []);

  const value: IGcalContext = {
    client: null,
    user: (user as unknown as GcalUser) ?? null,
    groups: role ? [role] : [],
    status: isPending
      ? GcalStatus.STARTUP
      : user
      ? GcalStatus.AUTHENTICATED
      : GcalStatus.UNAUTHENTICATED,
    signIn: async () => undefined,
    signOut,
    getSession,
  };

  return <GcalContext.Provider value={value}>{children}</GcalContext.Provider>;
}
